<template>
  <div
    class="tcard"
    :class="{ 'tcard--done': done, 'tcard--mobile': mobile }"
    :style="{ '--tc': color || '#9aa4b2' }"
    @click="desk.editTask('task', el.ref)"
  >
    <div v-if="!mobile" class="tcard__actions" @click.stop>
      <button type="button" :title="$t('share')" @click="desk.share(el.ref)">
        <v-icon x-small>mdi-share-variant-outline</v-icon>
      </button>
      <button type="button" :title="$t('copy')" @click="desk.copyTask('task', el.ref)">
        <v-icon x-small>mdi-content-copy</v-icon>
      </button>
      <button
        v-if="!done"
        type="button"
        class="tcard__ok"
        :title="$t('setDone')"
        @click="desk.setDoneStatus(el.ref)"
      >
        <v-icon x-small>mdi-check</v-icon>
      </button>
      <button v-if="el.ref.link" type="button" :title="$t('link')" @click="desk.openLink(el)">
        <v-icon x-small>mdi-link-variant</v-icon>
      </button>
      <button type="button" class="tcard__del" :title="$t('remove')" @click="desk.askDelete(el.ref)">
        <v-icon x-small>mdi-trash-can-outline</v-icon>
      </button>
    </div>

    <div class="tcard__top">
      <span class="tcard__title">{{ el.ref.title }}</span>
      <button
        v-if="mobile"
        type="button"
        class="tcard__more"
        @click.stop="$emit('more', el)"
      >
        <v-icon small>mdi-dots-vertical</v-icon>
      </button>
    </div>

    <div v-if="tags.length" class="tcard__tags">
      <span
        v-for="(tag, i) in tags"
        :key="i"
        class="tcard__tag"
        :style="{ background: tag.color }"
      >{{ tag.text }}</span>
    </div>

    <div v-if="metas.length" class="tcard__metas">
      <span
        v-for="(m, i) in metas"
        :key="i"
        class="tcard__meta"
        :class="{ 'tcard__meta--link': m.click }"
        @click.stop="m.click && m.click()"
      >
        <v-icon x-small>{{ m.icon }}</v-icon>
        <span class="tcard__meta-label">{{ m.label }}</span>
        <span class="tcard__meta-value">{{ m.value }}</span>
      </span>
    </div>

    <div class="tcard__foot">
      <span v-if="dueLabel" class="tcard__due" :class="dueClass">
        <v-icon x-small>mdi-calendar-blank-outline</v-icon>
        {{ dueLabel }}
      </span>
      <span v-if="el.ref.time" class="tcard__time" :title="el.user ? el.user.name : ''">
        {{ el.ref.time }}
      </span>
      <div v-if="people.length" class="tcard__people">
        <span
          v-for="(p, i) in people.slice(0, 3)"
          :key="i"
          class="tcard__avatar"
          :style="{ background: p.color || '#90a4ae' }"
          :title="p.name"
        >
          <img v-if="p.avatar" :src="p.avatar" alt="">
          <template v-else>{{ p.initials }}</template>
        </span>
        <span v-if="people.length > 3" class="tcard__avatar tcard__avatar--more">
          +{{ people.length - 3 }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { parseDate, today, fmt, diffDays } from '../deskDates'

export default {
  name: 'TaskCard',
  inject: ['desk'],
  props: {
    el: { type: Object, required: true },
    color: { type: String, default: null },
    done: { type: Boolean, default: false },
    mobile: { type: Boolean, default: false },
  },
  computed: {
    tags () {
      return (this.el.ref.tags || []).filter(Boolean)
    },
    metas () {
      const el = this.el
      const out = []
      if (el.relation) {
        out.push({
          icon: 'mdi-link-variant',
          label: '',
          value: el.relation.name,
          click: () => this.desk.openRelationLink(el),
        })
      }
      if (el.ref.functor) {
        out.push({ icon: 'mdi-account-hard-hat-outline', label: this.$t('functor') + ':', value: el.ref.functor.name })
      }
      if (el.ref.creator) {
        out.push({ icon: 'mdi-account-edit-outline', label: this.$t('maker') + ':', value: el.ref.creator.name })
      }
      const meta = el.relation && el.relation.meta
      if (meta && meta.branch) {
        out.push({ icon: 'mdi-source-branch', label: this.$t('branch-name') + ':', value: meta.branch.name })
      }
      if (meta && meta.referral_code) {
        out.push({ icon: 'mdi-ticket-confirmation-outline', label: this.$t('referral-code') + ':', value: meta.referral_code })
      }
      return out
    },
    people () {
      return (this.el.ref.participants || [])
        .filter(p => p && p.user)
        .map(p => {
          const u = p.user
          const first = u.firstname || ''
          const last = u.lastname || ''
          return {
            name: (first + ' ' + last).trim(),
            color: u.color,
            avatar: u.avatar && u.avatar.link,
            initials: (first.charAt(0) + last.charAt(0)) || '?',
          }
        })
    },
    due () {
      return parseDate(this.el.ref.due_end_date)
    },
    dueLabel () {
      if (!this.el.ref.due_end_date) return ''
      if (this.el.ref.timeRemianing && this.el.ref.timeRemianing.date) return this.el.ref.timeRemianing.date
      return this.due ? fmt(this.due, 'D MMMM', this.desk.jalali) : this.el.ref.due_end_date
    },
    dueClass () {
      if (this.done || !this.due) return ''
      const d = diffDays(this.due, today())
      if (d < 0) return 'tcard__due--late'
      if (d <= 1) return 'tcard__due--soon'
      return ''
    },
  },
}
</script>

<style scoped>
.tcard {
  position: relative;
  background: #fff;
  border: 1px solid #e6e9ef;
  border-radius: 8px;
  padding: 10px 12px 8px;
  padding-inline-start: 14px;
  cursor: pointer;
  white-space: normal;
  transition: box-shadow 0.15s, border-color 0.15s;
}
.tcard::before {
  content: '';
  position: absolute;
  inset-inline-start: 0;
  top: 10px;
  bottom: 10px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: var(--tc);
}
[dir='rtl'] .tcard::before,
.v-application--is-rtl .tcard::before {
  border-radius: 3px 0 0 3px;
}
.tcard:hover {
  border-color: #cfd6e0;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.08);
}
.theme--dark .tcard {
  background: #1e1e1e;
  border-color: #333;
}
.tcard--done .tcard__title {
  color: #8a94a6;
  text-decoration: line-through;
}
.tcard__top {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}
.tcard__title {
  flex: 1 1 auto;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.55;
  word-break: break-word;
}
.tcard__more {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  margin: -4px -6px 0;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.tcard__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}
.tcard__tag {
  font-size: 0.68rem;
  line-height: 18px;
  padding: 0 7px;
  border-radius: 4px;
  color: #fff;
}
.tcard__metas {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 6px;
}
.tcard__meta {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  color: #6b7280;
  min-width: 0;
}
.tcard__meta .v-icon {
  color: #9aa4b2;
}
.tcard__meta-label {
  flex: 0 0 auto;
}
.tcard__meta-value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #374151;
}
.theme--dark .tcard__meta-value {
  color: #ddd;
}
.tcard__meta--link .tcard__meta-value {
  color: var(--v-info-base, #1976d2);
}
.tcard__meta--link:hover .tcard__meta-value {
  text-decoration: underline;
}
.tcard__foot {
  display: flex;
  flex-wrap: wrap;
  row-gap: 4px;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  min-height: 24px;
}
.tcard__due {
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.7rem;
  padding: 0 6px;
  height: 20px;
  border-radius: 4px;
  background: #f1f3f7;
  color: #4b5563;
}
.tcard__due .v-icon {
  color: inherit;
}
.tcard__due--late {
  background: #fde8ec;
  color: #c2185b;
}
.tcard__due--soon {
  background: #fff4e0;
  color: #b26a00;
}
.theme--dark .tcard__due {
  background: #2a2a2a;
  color: #ccc;
}
.tcard__time {
  white-space: nowrap;
  font-size: 0.7rem;
  color: #9aa4b2;
}
/* Hover actions float over the top corner so the footer never reflows. */
.tcard__actions {
  position: absolute;
  top: 6px;
  inset-inline-end: 6px;
  z-index: 2;
  display: none;
  align-items: center;
  gap: 2px;
  padding: 2px;
  background: #fff;
  border: 1px solid #e6e9ef;
  border-radius: 7px;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.12);
}
.theme--dark .tcard__actions {
  background: #262626;
  border-color: #3a3a3a;
}
.tcard:hover .tcard__actions {
  display: inline-flex;
}
.tcard__actions button {
  width: 24px;
  height: 24px;
  border-radius: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.tcard__actions button:hover {
  background: #eef1f6;
}
.theme--dark .tcard__actions button:hover {
  background: #2c2c2c;
}
.tcard__actions .v-icon {
  color: #6b7280;
}
.tcard__actions .tcard__ok .v-icon {
  color: #2e7d32;
}
.tcard__actions .tcard__del .v-icon {
  color: #d81b60;
}
.tcard__people {
  flex: 0 0 auto;
  margin-inline-start: auto;
  display: inline-flex;
  padding-inline-start: 6px;
}
.tcard__avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #fff;
  margin-inline-start: -6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-size: 0.6rem;
  color: #fff;
  font-weight: 600;
}
.theme--dark .tcard__avatar {
  border-color: #1e1e1e;
}
.tcard__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.tcard__avatar--more {
  background: #e3e7ee;
  color: #4b5563;
}
.tcard--mobile {
  padding: 12px 12px 10px;
  padding-inline-start: 14px;
}
.tcard--mobile .tcard__title {
  font-size: 0.9rem;
}
</style>
